// OnboardingDetailPage-part3.tsx
// PART 3: Main Component Rendering Logic

import React from 'react';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  ThumbsUp,
  ThumbsDown
} from 'lucide-react';
import Navigation from '../components/common/Navigation';
import { OnboardingDetails } from '../types/onboarding';
import { safe, getStepIcon, getAdjudicationBadge, SubmissionStatusBadge, AdminReviewBadge, DualStatusDisplay } from './OnboardingDetailPage-part1';
import { StepCard, InfoItem, ImageCard, Modal } from './OnboardingDetailPage-part5';
import DocumentsStepReview from '../components/DocumentsStepReview';

interface RenderMainContentProps {
  onboarding: OnboardingDetails | null;
  loading: boolean;
  error: string;
  expandedSteps: Set<number>;
  showApprovalModal: boolean;
  setShowApprovalModal: (show: boolean) => void;
  showRejectionModal: boolean;
  setShowRejectionModal: (show: boolean) => void;
  showStepActionModal: boolean;
  setShowStepActionModal: (show: boolean) => void;
  selectedStep: string;
  actionType: 'approve' | 'reject';
  actionNotes: string;
  setActionNotes: (notes: string) => void;
  rejectionReason: string;
  setRejectionReason: (reason: string) => void;
  processing: boolean;
  toast?: { message: string; type: 'success' | 'error' } | null;
  toggleStep: (stepNumber: number) => void;
  handleStepAction: (stepName: string, action: 'approve' | 'reject') => void;
  executeStepAction: () => Promise<void>;
  handleFinalApproval: () => Promise<void>;
  handleFinalRejection: () => Promise<void>;
  navigate: any;
  loadOnboardingDetails?: (isRefresh?: boolean) => Promise<void>;
}

const formatDate = (date?: string) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const RenderMainContent: React.FC<RenderMainContentProps> = (props) => {
  const {
    onboarding,
    loading,
    error,
    expandedSteps,
    showApprovalModal,
    setShowApprovalModal,
    showRejectionModal,
    setShowRejectionModal,
    showStepActionModal,
    setShowStepActionModal,
    selectedStep,
    actionType,
    actionNotes,
    setActionNotes,
    rejectionReason,
    setRejectionReason,
    processing,
    toast,
    toggleStep,
    handleStepAction,
    executeStepAction,
    handleFinalApproval,
    handleFinalRejection,
    navigate,
    loadOnboardingDetails
  } = props;

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="onboarding-detail-page">
          <div className="loading-container">
            <div className="spinner-large"></div>
            <p>Loading onboarding details...</p>
          </div>
        </div>
      </>
    );
  }

  if (error || !onboarding) {
    return (
      <>
        <Navigation />
        <div className="onboarding-detail-page">
          <div className="error-container">
            <XCircle size={48} />
            <h2>Error Loading Details</h2>
            <p>{error || 'Onboarding record not found'}</p>
            <button onClick={() => navigate('/onboarding')} className="btn-primary">
              Back to List
            </button>
          </div>
        </div>
      </>
    );
  }

  // ✅ SAFE DESTRUCTURING with defaults
  const onb = onboarding?.onboarding || {} as any;
  const steps = onboarding?.steps || {} as any;

  return (
    <>
      <Navigation />
      <div className="onboarding-detail-page">
        {/* ✅ Toast Notification — replaces alert() to avoid scroll-to-top */}
        {toast && (
          <div style={{
            position: 'fixed',
            top: '80px',
            right: '24px',
            zIndex: 9999,
            padding: '14px 24px',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '600',
            color: 'white',
            background: toast.type === 'success' 
              ? 'linear-gradient(135deg, #10b981, #059669)' 
              : 'linear-gradient(135deg, #ef4444, #dc2626)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            animation: 'slideInRight 0.3s ease-out'
          }}>
            <span style={{ fontSize: '18px' }}>{toast.type === 'success' ? '✅' : '❌'}</span>
            {toast.message}
          </div>
        )}
        {/* Header */}
        <div className="detail-header">
          <button onClick={() => navigate('/onboarding')} className="btn-back">
            <ArrowLeft size={20} />
            Back to List
          </button>

          <div className="driver-info">
            <div className="driver-name">
              <h1>{safe(onb, 'user.firstName', '')} {safe(onb, 'user.lastName', '')}</h1>
              <div className="driver-contact">
                <span>{safe(onb, 'user.email', 'N/A')}</span>
                <span>•</span>
                <span>{safe(onb, 'user.phoneNumber', 'N/A')}</span>
              </div>
            </div>
            <div className="driver-status-badges">
              <span className={`status-badge status-${safe(onb, 'status', 'unknown').toLowerCase()}`}>
                {safe(onb, 'status', 'UNKNOWN')}
              </span>
              <span className={`status-badge status-${safe(onb, 'adminReviewStatus', 'pending').toLowerCase()}`}>
                {safe(onb, 'adminReviewStatus', 'PENDING')}
              </span>
            </div>
          </div>

          <div className="header-actions">
            <button
              onClick={() => setShowApprovalModal(true)}
              className="btn-success"
              disabled={Number(safe(onb, 'completionPercentage', 0)) !== 100 && Number(safe(onboarding, 'completionPercentage', 0)) !== 100 && Number(safe(onboarding, 'onboarding.completionPercentage', 0)) !== 100}
            >
              <ThumbsUp size={18} />
              Approve Driver
            </button>
            <button
              onClick={() => setShowRejectionModal(true)}
              className="btn-danger"
            >
              <ThumbsDown size={18} />
              Reject Driver
            </button>
          </div>
        </div>

        {/* Progress Overview */}
        <div className="progress-overview">
          <div className="progress-stats">
            <div className="stat">
              <div className="stat-label">Completion</div>
              <div className="stat-value">{safe(onb, 'completionPercentage', 0)}%</div>
            </div>
            <div className="stat">
              <div className="stat-label">Current Step</div>
              <div className="stat-value">Step {safe(onb, 'currentStep', 0)}/8</div>
            </div>
            <div className="stat">
              <div className="stat-label">Started</div>
              <div className="stat-value">{formatDate(safe(onb, 'startedAt'))}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Last Updated</div>
              <div className="stat-value">{formatDate(safe(onb, 'updatedAt'))}</div>
            </div>
          </div>
          <div className="progress-bar-container">
            <div className="progress-bar-large">
              <div
                className="progress-fill-large"
                style={{ width: `${safe(onb, 'completionPercentage', 0)}%` }}
              />
            </div>
          </div>
        </div>

        {/* Onboarding Steps */}
        <div className="steps-container">
          {/* Step 1: Profile */}
          <StepCard
            stepNumber={1}
            title="Driver Profile"
            icon={getStepIcon(1)}
            completed={safe(onb, 'profileCompleted', false)}
            status={safe(steps, 'profile.adminReviewStatus', 'pending_review')}
            onboardingStatus={safe(onb, 'status')}
            expanded={expandedSteps.has(1)}
            onToggle={() => toggleStep(1)}
            onApprove={() => {}}
            onReject={() => {}}
            hideStepActions={true}  // Profile is self-submitted — no admin action needed
          >
            {safe(steps, 'profile.data') ? (
              <div className="step-content">
                {/* Dual Status Display */}
                <DualStatusDisplay
                  submissionStatus={safe(steps, 'profile.status', 'not_started')}
                  adminReviewStatus={safe(steps, 'profile.adminReviewStatus', 'pending_review')}
                  stepName="Profile"
                />
                
                <div className="info-grid">
                  <InfoItem label="Name" value={`${safe(steps, 'profile.data.firstName', '')} ${safe(steps, 'profile.data.lastName', '')}`} />
                  <InfoItem label="Email" value={safe(steps, 'profile.data.email', 'N/A')} />
                  <InfoItem label="Phone" value={safe(steps, 'profile.data.phoneNumber', 'N/A')} />
                  <InfoItem label="Date of Birth" value={safe(steps, 'profile.data.dateOfBirth', 'N/A')} />
                  <InfoItem 
                    label="Email Verified" 
                    value={safe(steps, 'profile.emailVerified', false) ? '✅ Yes' : '❌ No'} 
                  />
                  <InfoItem 
                    label="Phone Verified" 
                    value={safe(steps, 'profile.data.phoneVerified', false) ? '✅ Yes' : '❌ No'} 
                  />
                  <InfoItem 
                    label="Address" 
                    value={safe(steps, 'profile.data.address.full', 'N/A')} 
                  />
                  <InfoItem label="Completed At" value={formatDate(safe(steps, 'profile.data.completedAt'))} />
                </div>
              </div>
            ) : (
              <p style={{ color: '#64748b', fontStyle: 'italic' }}>No profile data available</p>
            )}
          </StepCard>

          {/* Step 2: Identity Verification */}
          <StepCard
            stepNumber={2}
            title="Identity Verification"
            icon={getStepIcon(2)}
            completed={safe(onb, 'identityCompleted', false)}
            status={safe(steps, 'identity.adminReviewStatus', 'pending_review')}
            adjudication={safe(steps, 'identity.adjudication', 'pending')}
            onboardingStatus={safe(onb, 'status')}
            expanded={expandedSteps.has(2)}
            onToggle={() => toggleStep(2)}
            onApprove={() => {}}
            onReject={() => {}}
            hideStepActions={true}  // Identity verified by Certn automatically — no admin action needed
          >
            {safe(steps, 'identity.data') ? (
              <div className="step-content">
                {/* Dual Status Display */}
                <DualStatusDisplay
                  submissionStatus={safe(steps, 'identity.status', 'not_started')}
                  adminReviewStatus={safe(steps, 'identity.adminReviewStatus', 'pending_review')}
                  stepName="Identity Verification"
                />
                
                <div className="info-grid">
                  <InfoItem label="Verification Type" value={safe(steps, 'identity.data.verificationType', 'N/A')} />
                  {/* documentType is a DB enum value like "drivers_license" — format for humans */}
                  <InfoItem
                    label="Document Type"
                    value={(() => {
                      const raw = safe(steps, 'identity.data.documentType');
                      if (!raw) return 'Not captured';
                      const labels: Record<string, string> = {
                        drivers_license:        "Driver's License",
                        passport:               "Passport",
                        national_id:            "National ID",
                        health_card:            "Health Card",
                        permanent_resident_card:"Permanent Resident Card",
                      };
                      return labels[raw] || raw.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
                    })()}
                  />
                  {/* Age — show numerically with 18+ check result */}
                  <InfoItem
                    label="Driver Age"
                    value={(() => {
                      const age = safe(steps, 'identity.data.calculatedAge');
                      if (age === null || age === undefined) return 'Not calculated';
                      return `${age} years old`;
                    })()}
                  />
                  <InfoItem
                    label="Age Verified (18+)"
                    value={(() => {
                      const age = safe(steps, 'identity.data.calculatedAge');
                      const verified = safe(steps, 'identity.data.ageVerified');
                      if (age === null || age === undefined) return '⚠️ Age not available';
                      return verified ? `✅ Yes (${age})` : `❌ No (${age} — minimum 18 required)`;
                    })()}
                  />
                  <InfoItem label="Progress" value={`${safe(steps, 'identity.data.progress', 0)}%`} />
                  <InfoItem label="Verified At" value={formatDate(safe(steps, 'identity.verifiedAt'))} />
                </div>
                <div style={{ marginTop: '16px' }}>
                  <strong>Adjudication: </strong>
                  {getAdjudicationBadge(safe(steps, 'identity.adjudication', 'pending'))}
                </div>

                {/* ✅ IDENTITY VERIFICATION IMAGES */}
                {(safe(steps, 'identity.data.frontIdImage') || 
                  safe(steps, 'identity.data.backIdImage') || 
                  safe(steps, 'identity.data.selfieImage')) && (
                  <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '2px solid #e2e8f0' }}>
                    <h4 style={{ 
                      fontSize: '16px', 
                      fontWeight: '600', 
                      color: '#0f172a',
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        <circle cx="8.5" cy="8.5" r="1.5"></circle>
                        <polyline points="21 15 16 10 5 21"></polyline>
                      </svg>
                      Identity Documents
                    </h4>
                    
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', 
                      gap: '16px'
                    }}>
                      {safe(steps, 'identity.data.frontIdImage') && (
                        <ImageCard 
                          imagePath={safe(steps, 'identity.data.frontIdImage')}
                          label="ID Front"
                        />
                      )}

                      {safe(steps, 'identity.data.backIdImage') && (
                        <ImageCard 
                          imagePath={safe(steps, 'identity.data.backIdImage')}
                          label="ID Back"
                        />
                      )}

                      {safe(steps, 'identity.data.selfieImage') && (
                        <ImageCard 
                          imagePath={safe(steps, 'identity.data.selfieImage')}
                          label="Selfie Verification"
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p style={{ color: '#64748b', fontStyle: 'italic' }}>No identity verification data</p>
            )}
          </StepCard>

          {/* Step 3: Background Check */}
          <StepCard
            stepNumber={3}
            title="Background Check"
            icon={getStepIcon(3)}
            completed={safe(onb, 'backgroundCheckCompleted', false)}
            status={safe(steps, 'backgroundCheck.adminReviewStatus', 'pending_review')}
            adjudication={safe(steps, 'backgroundCheck.adjudication', 'pending')}
            onboardingStatus={safe(onb, 'status')}
            expanded={expandedSteps.has(3)}
            onToggle={() => toggleStep(3)}
            onApprove={() => {}}
            onReject={() => {}}
            hideStepActions={true}  // Background check verified by Certn automatically — no admin action needed
          >
            {safe(steps, 'backgroundCheck.data') ? (
              <div className="step-content">
                {/* Dual Status Display */}
                <DualStatusDisplay
                  submissionStatus={safe(steps, 'backgroundCheck.status', 'not_started')}
                  adminReviewStatus={safe(steps, 'backgroundCheck.adminReviewStatus', 'pending_review')}
                  stepName="Background Check"
                />
                
                <div className="info-grid">
                  <InfoItem label="Check Type" value={safe(steps, 'backgroundCheck.data.checkType', 'N/A')} />
                  <InfoItem label="Progress" value={`${safe(steps, 'backgroundCheck.data.progress', 0)}%`} />
                  <InfoItem label="Submitted At" value={formatDate(safe(steps, 'backgroundCheck.data.submittedAt'))} />
                  <InfoItem label="Completed At" value={formatDate(safe(steps, 'backgroundCheck.completedAt'))} />
                </div>
                <div style={{ marginTop: '16px' }}>
                  <strong>Adjudication: </strong>
                  {getAdjudicationBadge(safe(steps, 'backgroundCheck.adjudication', 'pending'))}
                </div>
              </div>
            ) : (
              <p style={{ color: '#64748b', fontStyle: 'italic' }}>No background check data</p>
            )}
          </StepCard>

          {/* Step 4: Driver Abstract */}
          <StepCard
            stepNumber={4}
            title="Driver Abstract"
            icon={getStepIcon(4)}
            completed={safe(onb, 'driverAbstractCompleted', false)}
            status={safe(steps, 'driverAbstract.adminReviewStatus', 'pending_review')}
            adjudication={safe(steps, 'driverAbstract.adjudication', 'pending')}
            onboardingStatus={safe(onb, 'status')}
            expanded={expandedSteps.has(4)}
            onToggle={() => toggleStep(4)}
            onApprove={() => {}}
            onReject={() => {}}
            hideStepActions={true}  // Driver abstract verified by Certn automatically — no admin action needed
          >
            {safe(steps, 'driverAbstract.data') ? (
              <div className="step-content">
                {/* Dual Status Display */}
                <DualStatusDisplay
                  submissionStatus={safe(steps, 'driverAbstract.status', 'not_started')}
                  adminReviewStatus={safe(steps, 'driverAbstract.adminReviewStatus', 'pending_review')}
                  stepName="Driver Abstract"
                />
                
                <div className="info-grid">
                  <InfoItem label="License Class" value={safe(steps, 'driverAbstract.data.licenseClass', 'N/A')} />
                  <InfoItem label="License Province" value={safe(steps, 'driverAbstract.data.licenseProvince', 'N/A')} />
                  <InfoItem label="License Status" value={safe(steps, 'driverAbstract.data.licenseStatus', 'N/A')} />
                  <InfoItem label="Demerit Points" value={safe(steps, 'driverAbstract.data.demeritPoints', 0).toString()} />
                  <InfoItem label="Total Violations" value={safe(steps, 'driverAbstract.data.totalViolations', 0).toString()} />
                  <InfoItem label="Major Violations" value={safe(steps, 'driverAbstract.data.majorViolations', 0).toString()} />
                  <InfoItem label="At-Fault Accidents" value={safe(steps, 'driverAbstract.data.atFaultAccidents', 0).toString()} />
                  <InfoItem label="Clean Record" value={safe(steps, 'driverAbstract.isClean', false) ? '✅ Yes' : '❌ No'} />
                </div>
                <div style={{ marginTop: '16px' }}>
                  <strong>Adjudication: </strong>
                  {getAdjudicationBadge(safe(steps, 'driverAbstract.adjudication', 'pending'))}
                </div>
              </div>
            ) : (
              <p style={{ color: '#64748b', fontStyle: 'italic' }}>No driver abstract data</p>
            )}
          </StepCard>

          {/* Step 5: Vehicle */}
          <StepCard
            stepNumber={5}
            title="Vehicle Information"
            icon={getStepIcon(5)}
            completed={safe(onb, 'vehicleCompleted', false)}
            status={safe(steps, 'vehicle.adminReviewStatus', 'pending_review')}
            onboardingStatus={safe(onb, 'status')}
            expanded={expandedSteps.has(5)}
            onToggle={() => toggleStep(5)}
            onApprove={() => handleStepAction('vehicle', 'approve')}
            onReject={() => handleStepAction('vehicle', 'reject')}
          >
            {safe(steps, 'vehicle.data') ? (
              <div className="step-content">
                {/* Dual Status Display */}
                <DualStatusDisplay
                  submissionStatus={safe(steps, 'vehicle.status', 'not_started')}
                  adminReviewStatus={safe(steps, 'vehicle.adminReviewStatus', 'pending_review')}
                  stepName="Vehicle Information"
                />

                {/* ✅ PREVIOUS REJECTION HISTORY */}
                {safe(steps, 'vehicle.adminReviewStatus') === 'pending_review' &&
                 (safe(steps, 'vehicle.data.rejectionReason') || safe(steps, 'vehicle.data.adminNotes')) && (
                  <div style={{
                    marginBottom: '20px',
                    padding: '16px',
                    background: '#fef3c7',
                    border: '2px solid #f59e0b',
                    borderRadius: '12px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '12px'
                    }}>
                      <span style={{ fontSize: '18px' }}>📋</span>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: '#92400e' }}>
                        Previous Rejection Details
                      </div>
                    </div>
                    {safe(steps, 'vehicle.data.rejectionReason') && (
                      <div style={{ marginBottom: '8px' }}>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: '#78350f', marginBottom: '4px' }}>
                          Rejection Reason:
                        </div>
                        <div style={{ fontSize: '13px', color: '#92400e' }}>
                          {safe(steps, 'vehicle.data.rejectionReason')}
                        </div>
                      </div>
                    )}
                    {safe(steps, 'vehicle.data.adminNotes') && (
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: '#78350f', marginBottom: '4px' }}>
                          Admin Notes:
                        </div>
                        <div style={{ fontSize: '13px', color: '#92400e' }}>
                          {safe(steps, 'vehicle.data.adminNotes')}
                        </div>
                      </div>
                    )}
                    <div style={{
                      marginTop: '12px',
                      padding: '8px 12px',
                      background: '#fed7aa',
                      borderRadius: '6px',
                      fontSize: '12px',
                      color: '#78350f',
                      fontStyle: 'italic'
                    }}>
                      ✅ Driver has resubmitted. Please verify the changes above.
                    </div>
                  </div>
                )}
                
                <div className="info-grid">
                  <InfoItem label="Make" value={safe(steps, 'vehicle.data.make', 'N/A')} />
                  <InfoItem label="Model" value={safe(steps, 'vehicle.data.model', 'N/A')} />
                  <InfoItem label="Year" value={safe(steps, 'vehicle.data.year', 'N/A')?.toString()} />
                  <InfoItem label="Color" value={safe(steps, 'vehicle.data.color', 'N/A')} />
                  <InfoItem label="License Plate" value={safe(steps, 'vehicle.data.licensePlate', 'N/A')} />
                  <InfoItem label="Province" value={safe(steps, 'vehicle.data.licensePlateProvince', 'N/A')} />
                  <InfoItem label="VIN" value={safe(steps, 'vehicle.data.vin', 'N/A')} />
                  <InfoItem label="Vehicle Type" value={safe(steps, 'vehicle.data.vehicleType', 'N/A')} />
                  <InfoItem label="Seating Capacity" value={safe(steps, 'vehicle.data.seatingCapacity', 'N/A')?.toString()} />
                  <InfoItem label="Insurance Provider" value={safe(steps, 'vehicle.data.insuranceProvider', 'N/A')} />
                  <InfoItem label="Insurance Policy #" value={safe(steps, 'vehicle.data.insurancePolicyNumber', 'N/A')} />
                  <InfoItem label="Insurance Expiry" value={formatDate(safe(steps, 'vehicle.data.insuranceExpiryDate'))} />
                  <InfoItem label="Registration #" value={safe(steps, 'vehicle.data.registrationNumber', 'N/A')} />
                  <InfoItem label="Registration Expiry" value={formatDate(safe(steps, 'vehicle.data.registrationExpiryDate'))} />
                </div>
              </div>
            ) : (
              <p style={{ color: '#64748b', fontStyle: 'italic' }}>No vehicle data</p>
            )}
          </StepCard>

          {/* Step 6: Documents - WITH IMAGE PREVIEW */}
          {/* ✅ NO STEP-LEVEL APPROVE/REJECT - Status is auto-calculated */}
          <StepCard
            stepNumber={6}
            title="Documents"
            icon={getStepIcon(6)}
            completed={safe(onb, 'documentsCompleted', false)}
            status={safe(steps, 'documents.adminReviewStatus', 'pending_review')}
            adminReviewStatus={safe(steps, 'documents.adminReviewStatus', 'pending_review')} // ✅ CRITICAL: Pass this prop!
            onboardingStatus={safe(onb, 'status')}
            expanded={expandedSteps.has(6)}
            onToggle={() => toggleStep(6)}
            onApprove={() => {}} // Required but not used
            onReject={() => {}}  // Required but not used
            hideStepActions={true} // ✅ Hide step-level approve/reject buttons
          >
            <div className="step-content">
              {/* Dual Status Display */}
              <DualStatusDisplay
                submissionStatus={safe(steps, 'documents.status', 'not_started')}
                adminReviewStatus={safe(steps, 'documents.adminReviewStatus', 'pending_review')}
                stepName="Documents"
              />
              
              {/* ✅ USE THE NEW COMPONENT FOR INDIVIDUAL DOCUMENT APPROVAL/REJECTION */}
              <DocumentsStepReview
                userId={safe(onb, 'userId', '')}
                documents={safe(steps, 'documents.data', [])}
                onUpdate={async () => {
                  // ✅ Silent refresh — no full page reload, preserves scroll position
                  if (loadOnboardingDetails) {
                    await loadOnboardingDetails(true);
                  }
                }}
              />
            </div>
          </StepCard>

          {/* Step 7: Vehicle Inspection */}
          <StepCard
            stepNumber={7}
            title="Vehicle Inspection"
            icon={getStepIcon(7)}
            completed={safe(onb, 'inspectionCompleted', false)}
            status={safe(steps, 'inspection.adminReviewStatus', 'pending_review')}
            onboardingStatus={safe(onb, 'status')}
            expanded={expandedSteps.has(7)}
            onToggle={() => toggleStep(7)}
            onApprove={() => handleStepAction('inspection', 'approve')}
            onReject={() => handleStepAction('inspection', 'reject')}
          >
            {safe(steps, 'inspection') ? (
              <div className="step-content">
                {/* Dual Status Display */}
                <DualStatusDisplay
                  submissionStatus={safe(steps, 'inspection.submissionStatus', safe(steps, 'inspection.status', 'not_started'))}
                  adminReviewStatus={safe(steps, 'inspection.adminReviewStatus', 'pending_review')}
                  stepName="Vehicle Inspection"
                />

                <div className="info-grid">
                  <InfoItem 
                    label="Progress" 
                    value={`${safe(steps, 'inspection.progress.completed', 0)}/${safe(steps, 'inspection.progress.total', 8)} items uploaded`} 
                  />
                  <InfoItem label="Submitted At" value={formatDate(safe(steps, 'inspection.submittedAt'))} />
                  <InfoItem label="Reviewed At" value={formatDate(safe(steps, 'inspection.reviewedAt'))} />
                  <InfoItem label="Approved At" value={formatDate(safe(steps, 'inspection.approvedAt'))} />
                  <InfoItem 
                    label="Odometer Reading" 
                    value={safe(steps, 'inspection.odometerReading') ? `${safe(steps, 'inspection.odometerReading').toLocaleString()} km` : 'Not entered'} 
                  />
                  <InfoItem 
                    label="VIN Number" 
                    value={safe(steps, 'inspection.vinNumber', 'Not entered')} 
                  />
                  <InfoItem 
                    label="SSC Document" 
                    value={safe(steps, 'inspection.data.sscDocument') || safe(steps, 'inspection.sscDocument') ? '✅ Uploaded' : '❌ Not uploaded'} 
                  />
                  <InfoItem 
                    label="SSC Expiry Date" 
                    value={formatDate(safe(steps, 'inspection.sscExpiryDate') || safe(steps, 'inspection.data.sscExpiryDate'))} 
                  />
                </div>

                {/* Rejection reason */}
                {safe(steps, 'inspection.rejectionReason') && (
                  <div style={{
                    marginTop: '16px',
                    padding: '12px',
                    background: '#fee2e2',
                    borderRadius: '8px',
                    border: '1px solid #fca5a5'
                  }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#991b1b', marginBottom: '4px' }}>
                      Rejection Reason:
                    </div>
                    <div style={{ fontSize: '13px', color: '#991b1b' }}>
                      {safe(steps, 'inspection.rejectionReason')}
                    </div>
                  </div>
                )}

                {/* ✅ VEHICLE INSPECTION PHOTOS GALLERY */}
                {(safe(steps, 'inspection.data') || safe(steps, 'inspection.id')) && (
                  <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '2px solid #e2e8f0' }}>
                    <h4 style={{ 
                      fontSize: '16px', 
                      fontWeight: '600', 
                      color: '#0f172a',
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                        <circle cx="12" cy="13" r="4"></circle>
                      </svg>
                      Inspection Photos & Documents
                    </h4>
                    
                    {(() => {
                      const inspectionId = safe(steps, 'inspection.id') || safe(steps, 'inspection.data.id');
                      if (!inspectionId) return <p style={{ color: '#94a3b8', fontSize: '13px' }}>Inspection ID not available</p>;

                      const photoTypes = [
                        { field: 'photoFront', label: 'Front Exterior' },
                        { field: 'photoRear', label: 'Rear Exterior' },
                        { field: 'photoLeftSide', label: 'Left Side' },
                        { field: 'photoRightSide', label: 'Right Side' },
                        { field: 'photoInterior', label: 'Interior' },
                        { field: 'photoOdometer', label: 'Odometer' },
                        { field: 'photoVin', label: 'VIN Number' },
                        { field: 'sscDocument', label: 'Safety Standards Certificate' },
                      ];

                      return (
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
                          gap: '16px'
                        }}>
                          {photoTypes.map(({ field, label }) => {
                            const hasPhoto = safe(steps, `inspection.data.${field}`) || safe(steps, `inspection.${field}`);
                            if (!hasPhoto) return null;
                            return (
                              <ImageCard
                                key={`${inspectionId}-${field}`}
                                imagePath={`/onboarding/inspection/${inspectionId}/photo/${field}`}
                                label={label}
                              />
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            ) : (
              <p style={{ color: '#64748b', fontStyle: 'italic' }}>No inspection data</p>
            )}
          </StepCard>

          {/* Step 8: Bank Account */}
          <StepCard
            stepNumber={8}
            title="Bank Account"
            icon={getStepIcon(8)}
            completed={safe(onb, 'bankAccountCompleted', false)}
            status={safe(steps, 'bankAccount.adminReviewStatus', 'pending_review')}
            onboardingStatus={safe(onb, 'status')}
            expanded={expandedSteps.has(8)}
            onToggle={() => toggleStep(8)}
            onApprove={() => handleStepAction('bankAccount', 'approve')}
            onReject={() => handleStepAction('bankAccount', 'reject')}
          >
            {safe(steps, 'bankAccount.data') ? (
              <div className="step-content">
                {/* Dual Status Display */}
                <DualStatusDisplay
                  submissionStatus={safe(steps, 'bankAccount.status', 'not_started')}
                  adminReviewStatus={safe(steps, 'bankAccount.adminReviewStatus', 'pending_review')}
                  stepName="Bank Account"
                />
                
                <div className="info-grid">
                  <InfoItem label="Bank Name" value={safe(steps, 'bankAccount.data.bankName', 'N/A')} />
                  <InfoItem label="Account Type" value={safe(steps, 'bankAccount.data.accountType', 'N/A')} />
                  <InfoItem label="Account Number" value={safe(steps, 'bankAccount.data.maskedAccount', 'N/A')} />
                  <InfoItem 
                    label="Stripe Connected" 
                    value={safe(steps, 'bankAccount.data.isStripeConnected', false) ? '✅ Yes' : '❌ No'} 
                  />
                  <InfoItem 
                    label="Verified" 
                    value={safe(steps, 'bankAccount.data.isVerified', false) ? '✅ Yes' : '❌ No'} 
                  />
                  <InfoItem label="Verified At" value={formatDate(safe(steps, 'bankAccount.data.verifiedAt'))} />
                  <InfoItem label="Verification Method" value={safe(steps, 'bankAccount.data.verificationMethod', 'N/A')} />
                </div>
              </div>
            ) : (
              <p style={{ color: '#64748b', fontStyle: 'italic' }}>No bank account data</p>
            )}
          </StepCard>
        </div>

        {/* Modals */}
        {showStepActionModal && (
          <Modal
            title={`${actionType === 'approve' ? 'Approve' : 'Reject'} Step: ${selectedStep}`}
            onClose={() => {
              setShowStepActionModal(false);
            }}
          >
            {actionType === 'reject' && (
              <div className="form-group">
                <label>Rejection Reason *</label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Explain why this step is being rejected..."
                  rows={4}
                />
              </div>
            )}
            <div className="form-group">
              <label>Admin Notes (Optional)</label>
              <textarea
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                placeholder="Add any additional notes..."
                rows={3}
              />
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowStepActionModal(false)} className="btn-secondary">
                Cancel
              </button>
              <button
                onClick={executeStepAction}
                className={actionType === 'approve' ? 'btn-success' : 'btn-danger'}
                disabled={processing}
              >
                {processing ? 'Processing...' : actionType === 'approve' ? 'Approve Step' : 'Reject Step'}
              </button>
            </div>
          </Modal>
        )}

        {showApprovalModal && (
          <Modal title="Approve Entire Onboarding" onClose={() => setShowApprovalModal(false)}>
            <p>This will approve the driver to start accepting rides. Are you sure all steps are verified?</p>
            <div className="form-group">
              <label>Final Notes (Optional)</label>
              <textarea
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                placeholder="Add any final notes..."
                rows={3}
              />
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowApprovalModal(false)} className="btn-secondary">
                Cancel
              </button>
              <button onClick={handleFinalApproval} className="btn-success" disabled={processing}>
                {processing ? 'Approving...' : 'Approve Driver'}
              </button>
            </div>
          </Modal>
        )}

        {showRejectionModal && (
          <Modal title="Reject Entire Onboarding" onClose={() => setShowRejectionModal(false)}>
            <p>This will reject the driver's onboarding application. Please provide a reason.</p>
            <div className="form-group">
              <label>Rejection Reason *</label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Explain why the onboarding is being rejected..."
                rows={4}
              />
            </div>
            <div className="form-group">
              <label>Additional Notes (Optional)</label>
              <textarea
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                placeholder="Add any additional notes..."
                rows={3}
              />
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowRejectionModal(false)} className="btn-secondary">
                Cancel
              </button>
              <button onClick={handleFinalRejection} className="btn-danger" disabled={processing}>
                {processing ? 'Rejecting...' : 'Reject Driver'}
              </button>
            </div>
          </Modal>
        )}
      </div>
    </>
  );
};